import React from "react"
import {
  motion,
  useViewportScroll,
  useTransform,
  useSpring,
} from "framer-motion"
import { Container, Title, Svg } from "./logo.styles"

function getArcPath(cx, cy, rx, ry, startAngle, sweepAngle) {
  // 0 degrees, no path
  if (!sweepAngle) return `M${cx} ${cy}`

  // mod angles to 360 degrees
  startAngle %= 360
  sweepAngle %= 360

  // If arc is 360 degrees, the sweep angle is 0 due to mod
  var isClosed = sweepAngle === 0

  // If closed, we'll need to use 2 arcs with an angle of 180 degrees
  if (isClosed) {
    sweepAngle = 180
  }

  // Make angle positive to simplify large arc flag
  if (sweepAngle < 0) {
    sweepAngle += 360
  }

  var largeArc = sweepAngle > 180 ? 1 : 0

  // Convert degrees to radians
  var a1 = (startAngle * Math.PI) / 180
  var a2 = ((startAngle + sweepAngle) * Math.PI) / 180

  // Calculate start and end coords for arc. Starts at 12 o'clock
  var x1 = cx + rx * Math.sin(a1)
  var y1 = cy - ry * Math.cos(a1)

  var x2 = cx + rx * Math.sin(a2)
  var y2 = cy - ry * Math.cos(a2)

  /*
  NOTE: To orient the starting point to 3 o'clock instead of 12 o'clock,
  add 90 degrees to the start angle, or change the code above to this...

  var x1 = cx + rx * Math.cos(a1);   
  var y1 = cy + ry * Math.sin(a1);

  var x2 = cx + rx * Math.cos(a2); 
  var y2 = cy + ry * Math.sin(a2);
  */

  // Related demo using arc path
  // https://codepen.io/osublake/pen/VbWKMj/
  if (isClosed) {
    return `
      M ${x1} ${y1}
      A ${rx} ${ry} 0 1 1 ${x2} ${y2}
      A ${rx} ${ry} 0 1 1 ${x1} ${y1}z`
  }

  return `
    M ${x1} ${y1}
    A ${rx} ${ry} 0 ${largeArc} 1 ${x2} ${y2}
    L ${cx} ${cy}z`
}

const arcConfig = {
  startAngle: -78,
  sweepAngle: 78,
  cx: 50,
  cy: 50,
  rx: 50,
  ry: 50,
}

export const Logo = ({ color }) => {
  const { scrollYProgress } = useViewportScroll()
  const transformedY = useTransform(scrollYProgress, [0, 1], [78, 360])
  const animatedY = useSpring(transformedY, { stiffness: 400, damping: 60 })
  const d = useTransform(animatedY, (value) =>
    getArcPath(
      arcConfig.cx,
      arcConfig.cy,
      arcConfig.rx,
      arcConfig.ry,
      arcConfig.startAngle,
      value
    )
  )

  return (
    <Container to="/">
      <Title>
        <Svg
          id="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          color={color}
        >
          <defs>
            <polygon
              id="path"
              points="0 0.00025 99.212 0.00025 99.212 99.21225 0 99.21225"
            ></polygon>
          </defs>
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g>
              <mask id="mask" fill="#fff">
                <use xlinkHref="#path"></use>
              </mask>
              <path
                d="M49.9195,88.77975 C71.277,88.60975 88.6095,71.27475 88.777,49.91725 L92.142,49.91725 C91.972,73.13225 73.132,91.97475 49.9195,92.14475 L49.9195,88.77975 Z M25.972,49.91725 C25.972,49.97475 25.9645,50.02725 25.9645,50.08475 C25.9645,63.16725 37.2945,72.78475 49.2945,73.04225 L49.2945,88.15475 C28.277,87.98725 11.222,70.93475 11.0545,49.91725 L25.972,49.91725 Z M49.2945,92.14475 C26.0795,91.97725 7.2345,73.13475 7.067,49.91725 L10.4295,49.91725 C10.597,71.27725 27.9345,88.61225 49.2945,88.77975 L49.2945,92.14475 Z M11.192,41.84225 C10.7045,44.25475 10.4495,46.74725 10.4295,49.29225 L7.067,49.29225 C7.087,46.51975 7.367,43.80475 7.8945,41.17725 L11.192,41.84225 Z M26.582,44.95475 C26.2495,46.35725 26.0595,47.80725 26.007,49.29225 L11.0545,49.29225 C11.0745,46.78725 11.327,44.33725 11.8045,41.96725 L26.582,44.95475 Z M49.9195,49.29225 L49.9195,31.13225 C56.2345,31.17725 62.5295,34.51975 65.567,39.70225 L70.7295,39.70225 C68.1645,33.50475 59.797,27.19225 49.9195,27.10975 L49.9195,11.05725 C70.932,11.22975 87.9845,28.27975 88.152,49.29225 L49.9195,49.29225 Z M49.9195,55.90475 L67.907,55.90475 C65.867,63.58475 58.007,69.04725 49.967,69.04725 C49.952,69.04725 49.9345,69.04475 49.9195,69.04475 L49.9195,55.90475 Z M48.052,49.29225 L30.4495,49.29225 C30.5045,48.10725 30.662,46.94975 30.9295,45.83225 L48.052,49.29225 Z M49.2945,49.91725 L49.2945,51.88475 L43.127,51.88475 L43.127,55.90475 L49.2945,55.90475 L49.2945,69.01475 C39.0295,68.67475 30.4045,60.65475 30.4045,50.14475 C30.4045,50.06725 30.417,49.99475 30.417,49.91725 L49.2945,49.91725 Z M49.9195,73.05975 C62.2195,72.99475 72.292,63.78225 73.2495,51.88475 L49.9195,51.88475 L49.9195,49.91725 L88.152,49.91725 C87.9845,70.93225 70.932,87.98475 49.9195,88.15475 L49.9195,73.05975 Z M49.9195,7.06975 C73.132,7.24225 91.9695,26.07975 92.142,49.29225 L88.777,49.29225 C88.6095,27.93475 71.277,10.60475 49.9195,10.43225 L49.9195,7.06975 Z M93.392,49.29225 C93.2195,25.39225 73.822,5.99225 49.9195,5.81975 L49.9195,-0.00025 L49.607,-0.00025 L49.2945,-0.00025 L49.2945,0.01225 C25.402,0.16225 5.5195,17.18975 0.9795,39.77975 L6.672,40.92975 C6.1245,43.63725 5.837,46.43225 5.817,49.29225 L-0.0005,49.29225 L-0.0005,49.91725 L5.817,49.91725 C5.987,73.82475 25.3895,93.22725 49.2945,93.39475 L49.2945,99.21225 L49.9195,99.21225 L49.9195,93.39475 C73.822,93.22475 93.222,73.82225 93.392,49.91725 L99.212,49.91725 L99.212,49.29225 L93.392,49.29225 Z"
                fill={color}
                mask="url(#mask)"
              ></path>
            </g>
          </g>

          <motion.path id="arc-path" d={d} />
        </Svg>
      </Title>
    </Container>
  )
}

Logo.defaultProps = {
  color: "#fff",
}

export default Logo
